import axios, { AxiosError } from 'axios'
import { toast } from 'sonner'

import { API_BASE_URL } from '@/config'
import { queryClient } from '@/config/query-client'
import { useAuthStore } from '@/stores/auth.store'
import { usePermissionStore } from '@/stores/permission.store'
import { useSubscriptionStore } from '@/stores/subscription.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import { refreshResponseSchema, type RefreshResponse } from '@/types/api/auth'
import { logger } from '@/utils/logger'

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Don't try the refresh-cookie flow when this request 401s. Set for requests that carry a
     * login token: refreshing can't fix an expired login token and would only loop.
     */
    skipAuthRefresh?: boolean
    /** Don't show the global error toast for this request; the caller reports the error itself. */
    suppressErrorToast?: boolean
  }
}

export class ApiError {
  constructor(
    public type: string,
    public title: string,
    public status: number,
    public detail: string,
    public errors?: Record<string, string[]>,
    /** Full problem-detail body, including machine-readable extension fields. */
    public body?: Record<string, unknown>
  ) {}

  static fromAxiosError(error: AxiosError): ApiError {
    const data = error.response?.data as Record<string, unknown> | undefined
    return new ApiError(
      (data?.type as string) || 'server-error',
      (data?.title as string) || 'Server Error',
      error.response?.status || 500,
      (data?.detail as string) || 'An unexpected error occurred',
      data?.errors as Record<string, string[]> | undefined,
      data
    )
  }
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

/**
 * True when a request failed because the server couldn't be reached or errored (e.g. while the
 * backend restarts), as opposed to rejecting the credentials. Such failures must not end the session.
 */
export function isServerUnavailable(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500
  if (axios.isAxiosError(error)) return !error.response || error.response.status >= 500
  return false
}

let refreshInFlight: Promise<RefreshResponse> | null = null

/**
 * Exchanges the refresh cookie for a new access token and restores the session identity from it.
 * Concurrent callers share one request: the backend rotates refresh tokens, so a parallel refresh
 * would present an already-revoked token and end the session.
 */
export function refreshSession(): Promise<RefreshResponse> {
  refreshInFlight ??= axios
    .post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true })
    .then((response) => {
      const session = refreshResponseSchema.parse(response.data)
      const auth = useAuthStore.getState()
      auth.setAccessToken(session.accessToken)
      if (session.userId) auth.setUserId(session.userId)
      if (session.email) auth.setEmail(session.email)

      const workspace = useWorkspaceStore.getState()
      if (session.workspaceId && session.workspaceId !== workspace.currentWorkspaceId) {
        // Name is filled in from the workspace list once it loads.
        workspace.setWorkspace(session.workspaceId, '')
      }

      const permissions = usePermissionStore.getState()
      permissions.setIsSuperUser(session.superUser)
      permissions.setIsDeveloper(session.developerRole)
      return session
    })
    .finally(() => {
      refreshInFlight = null
    })
  return refreshInFlight
}

function endSession() {
  useAuthStore.getState().logout()
  queryClient.clear()
  // Loaded on demand: the push code imports this client. Drop the token before the page reloads.
  void import('@/features/notification/push/dropPushToken')
    .then(({ dropPushToken }) => dropPushToken())
    .catch(() => undefined)
    .finally(() => {
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    })
}

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config

    if (error.response?.status === 401) {
      const data = error.response?.data as Record<string, unknown> | undefined
      const errorType = (data?.type as string) || ''

      if (
        errorType === 'invalid-token' &&
        originalRequest &&
        !originalRequest.skipAuthRefresh &&
        !originalRequest.url?.includes('/auth/refresh')
      ) {
        try {
          const { accessToken } = await refreshSession()
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return apiClient(originalRequest)
        } catch (refreshError) {
          // An unreachable server says nothing about the session; keep it for the next attempt.
          if (!isServerUnavailable(refreshError)) {
            endSession()
          }
          return Promise.reject(refreshError)
        }
      }

      if (errorType === 'authentication' || errorType === 'invalid-token') {
        // If this is a login request, let the error through to the component
        if (!originalRequest?.url?.includes('/auth/login')) {
          endSession()
        }
      }
    }

    if (error.response?.status === 403) {
      const data = error.response?.data as Record<string, unknown> | undefined
      const errorType = (data?.type as string) || ''

      if (errorType === 'forbidden' || errorType === 'policy-violation') {
        logger.warn('Permission denied:', data?.detail)
      }
    }

    if (error.response?.status === 402 || error.response?.status === 409) {
      const data = error.response?.data as Record<string, unknown> | undefined
      const errorType = (data?.type as string) || ''
      const subscriptionErrorTypes = ['quota-exceeded', 'subscription-expired', 'resource-locked'] as const
      type SubscriptionErrorType = (typeof subscriptionErrorTypes)[number]

      if (subscriptionErrorTypes.includes(errorType as SubscriptionErrorType)) {
        useSubscriptionStore
          .getState()
          .setSubscriptionError(errorType as SubscriptionErrorType, data?.detail as string)
      }
    }

    // Global error toast handler
    if (error.response?.status && !originalRequest?.suppressErrorToast) {
      // Don't show toast for 401 (auth errors handled above), 402/409 (subscription errors), 403 (permission
      // checks the UI acts on itself), or 404 (resource not found)
      if (![401, 402, 403, 404, 409].includes(error.response.status)) {
        const apiError = ApiError.fromAxiosError(error)
        const errorMessage = `${apiError.title}${apiError.detail ? ': ' + apiError.detail : ''}`
        toast.error(errorMessage, {
          description: undefined,
        })
      }
    }

    return Promise.reject(ApiError.fromAxiosError(error))
  }
)

export { apiClient }
export default apiClient